import axios, { type AxiosRequestConfig, type AxiosResponse } from 'axios'
import { errorToast, successToast } from '@/services/toast'

function setupAxiosInterceptors() {
    // Request interceptor to modify config before sending the request
    axios.interceptors.request.use(
        (config) => {
            // Optionally add custom headers, like Authorization token
            const token = localStorage.getItem("authToken");
            if (token) {
                config.headers.Authorization = `Bearer ${token}`;
            }
            return config;
        },
        (error) => {
            // Handle request error
            return Promise.reject(error);
        }
    );

    // Response interceptor to handle 401 errors
    axios.interceptors.response.use(
        (response) => response, // Pass through successful responses
        async (error) => {
            // If it's not a 401 or refresh fails, reject the error
            return Promise.reject(error);
        }
    );
}

async function apiRequest(
    method: AxiosRequestConfig['method'],
    url: string,
    data: Record<string, any> | FormData = {}, // Allow FormData
    options: AxiosRequestConfig = {}
): Promise<AxiosResponse> {
    try {
        const isFormData = data instanceof FormData;

        const response = await axios({
            method,
            url: import.meta.env.VITE_API_BASE_URL + url,
            data,
            headers: {
                ...(isFormData ? { "Content-Type": "multipart/form-data" } : {}),
                ...options.headers,
            },
            ...options
        });

        if (response.data?.message) {
            successToast(response.data.message);
        }

        return response;
    } catch (error: any) {
        if (axios.isAxiosError(error)) {
            if (error.response) {
                const { status, data } = error.response;

                switch (status) {
                    case 422:
                        if (data?.data?.error != 'Unauthorized') {
                            errorToast(422, data?.data?.errors);
                        }
                        break;
                    case 429:
                        errorToast(429, ['Too many attempts, please try again later']);
                        break;
                    case 500:
                        errorToast(500, ['There was a system error']);
                        break;
                    default:
                        console.error(error);
                }
            } else {
                console.error('No response received:', error);
            }
        } else {
            console.error('Non-Axios error:', error);
        }

        throw error;
    }
}

export { setupAxiosInterceptors, apiRequest }